import React from 'react';
import styled from 'styled-components';
import Button from '@material-ui/core/Button';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Checkbox from '@material-ui/core/Checkbox';
import gql from 'graphql-tag';
import { useQuery, useMutation } from '@apollo/react-hooks';
import CircularProgress from '@material-ui/core/CircularProgress';
import moment from 'moment';

import Tag from 'components/common/tag';
import Pagination from 'components/common/pagination';
import Modal from 'components/common/modal';
import { ReactComponent as AddIcon } from 'assets/vectors/add-green-16px.svg';
import useEquipmentStatus from 'helpers/useEquipmentStatus';
import Filters from 'components/common/equipment/filters';
import { ReactComponent as OrderDownIcon } from 'assets/vectors/order-down-16px.svg';
import useExpirationColor from 'helpers/useExpirationColor';

const HoseBunsQuery = gql`
  query HoseBuns ($first: Int, $after: String, $last: Int, $before: String, $sortBy: [String], $status: String, $diameter: Float, $type: String, $location: String){
    hoseBuns(first: $first, after: $after, last: $last, before: $before, sortBy: $sortBy, status: $status, diameter: $diameter, type: $type, location: $location) {
      pageInfo {
        hasNextPage
        hasPreviousPage
        endCursor
        startCursor
      }
      edges {
        node {
          id
          serialNumber
          plNumber
          status
          location
          diameter
          type
          expireDate
          lastProducts
        }
      }
    }
  }
`;

const AddHoseBunsMutation = gql`
  mutation AddStsProjectEquipment($input: AddStsProjectEquipmentInput!) {
    addStsProjectEquipment(addStsProjectEquipmentData: $input) {
      success
    }
  }
`;

const resultCount = 10;

function HoseBunsModal({ setShowHoseBunsModal, projectId, refetch, addedHoseBuns, onAdd }) {
  const [selectedHoseBuns, setSelectedHoseBuns] = React.useState([]);
  const [paginationVariables, setPaginationVariables] = React.useState({first: resultCount});
  const {tagColor, equipmentStatusLabel} = useEquipmentStatus();
  const [filters, setFilters] = React.useState([]);
  const [sortBy, setSortBy] = React.useState({ type: 'plNumber', direction: 'asc' });
  const {textColor} = useExpirationColor();

  const filterVariables = React.useMemo(() => {
    return filters.reduce((prev, cur) => {
      return {
        ...prev,
        [cur.key]: cur.val,
      }
    }, {});
  }, [filters]);

  const { data, loading: listLoading } = useQuery(HoseBunsQuery, {
    variables: {
      ...paginationVariables,
      ...filterVariables,
      sortBy: [`${sortBy.type} ${sortBy.direction}`]
    },
    fetchPolicy: 'network-only',
  });

  const [addHoseBuns, { loading: addLoading }] = useMutation(AddHoseBunsMutation, {
    variables: {
      input: {
        stsprojectId: projectId,
        equipmentIds: selectedHoseBuns,
        equipmentType: 'hose_bun',
      },
    },
    onCompleted() {
      refetch();
      onAdd && onAdd();
      setShowHoseBunsModal(false);
    },
    onError(e) {
      console.log(e)
    }
  });

  const hoseBuns = data ? data.hoseBuns.edges.map(u => u.node) : [];
  const pageInfo = data ? data.hoseBuns.pageInfo : {};

  function checkboxHandler(hoseBunId) {
    if (selectedHoseBuns.includes(hoseBunId)) {
      setSelectedHoseBuns(selectedHoseBuns.filter(id => id !== hoseBunId));
    } else {
      setSelectedHoseBuns([...selectedHoseBuns, hoseBunId]);
    }
  }

  function checkboxDisabled(hoseBun) {
    return addedHoseBuns.includes(hoseBun.id) || hoseBun.status !== 'stand_by';
  }

  function sortHandler(type) {
    if (type !== sortBy.type) {
      setSortBy({ type, direction: 'desc' });
    } else if (type === sortBy.type && sortBy.direction === 'desc') {
      setSortBy({ ...sortBy, direction: 'asc'});
    } else if (type === sortBy.type && sortBy.direction === 'asc') {
      setSortBy({ ...sortBy, direction: 'desc'});
    }
  }

  const addButtonDisabled = listLoading || addLoading || !selectedHoseBuns.length

  return (
    <Modal onClose={() => setShowHoseBunsModal(false)} title="HOSE BUNS" style={{ width: '90%'}} preventOutsideClick>
      <Title>ADD HOSE BUN</Title>
      <Filters filters={filters} setFilters={setFilters} equipmentType="hose_bun" />
      {
        listLoading
          ? <CircularProgress size={64} style={{ margin: '128px auto', display: 'block' }} />
          : (
            <>
              <TableContainer>
                <Table aria-label="simple table" style={{ whiteSpace: 'nowrap' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell />
                      <TableCell style={{ cursor: 'pointer'}} onClick={() => sortHandler('plNumber')}>
                        PL Nr. <StyledOrderIcon active={sortBy.type === 'plNumber' ? 1 : 0} direction={sortBy.direction} />
                      </TableCell>
                      <TableCell style={{ cursor: 'pointer'}} onClick={() => sortHandler('serialNumber')}>
                        Serial Nr. <StyledOrderIcon active={sortBy.type === 'serialNumber' ? 1 : 0} direction={sortBy.direction} />
                      </TableCell>
                      <TableCell style={{ cursor: 'pointer'}} onClick={() => sortHandler('diameter')}>
                        Diameter <StyledOrderIcon active={sortBy.type === 'diameter' ? 1 : 0} direction={sortBy.direction} />
                      </TableCell>
                      <TableCell style={{ cursor: 'pointer'}} onClick={() => sortHandler('location')}>
                        Location <StyledOrderIcon active={sortBy.type === 'location' ? 1 : 0} direction={sortBy.direction} />
                      </TableCell>
                      <TableCell style={{ cursor: 'pointer'}} onClick={() => sortHandler('type')}>
                        Type <StyledOrderIcon active={sortBy.type === 'type' ? 1 : 0} direction={sortBy.direction} />
                      </TableCell>
                      <TableCell style={{ cursor: 'pointer'}} onClick={() => sortHandler('status')}>
                        Status <StyledOrderIcon active={sortBy.type === 'status' ? 1 : 0} direction={sortBy.direction} />
                      </TableCell>
                      <TableCell style={{ cursor: 'pointer'}} onClick={() => sortHandler('expireDate')}>
                        Expire date <StyledOrderIcon active={sortBy.type === 'expireDate' ? 1 : 0} direction={sortBy.direction} />
                      </TableCell>
                      <TableCell>
                        Last products
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                  {
                    !hoseBuns.length
                      ? <TableRow>
                          <TableCell>No results</TableCell>
                          <TableCell />
                          <TableCell />
                          <TableCell />
                          <TableCell />
                          <TableCell />
                          <TableCell />
                          <TableCell />
                          <TableCell />
                        </TableRow>
                      : (
                        hoseBuns.map(hoseBun => (
                          <TableRow key={hoseBun.id}>
                            <TableCell>
                              <Checkbox
                                checked={selectedHoseBuns.includes(hoseBun.id)}
                                onChange={() => checkboxHandler(hoseBun.id)}
                                name="Now"
                                color="primary"
                                disabled={checkboxDisabled(hoseBun)}
                              />
                            </TableCell>
                            <TableCell>
                              <Link target="_blank" href={`/dashboard/equipment/hoseBuns/${hoseBun.id}`}>{hoseBun.plNumber}</Link>
                            </TableCell>
                            <TableCell>
                              {hoseBun.serialNumber}
                            </TableCell>
                            <TableCell>
                              {hoseBun.diameter}
                            </TableCell>
                            <TableCell>
                              {hoseBun.location}
                            </TableCell>
                            <TableCell>
                              {hoseBun.type}
                            </TableCell>
                            <TableCell>
                              <Tag color={tagColor(hoseBun.status)} label={equipmentStatusLabel(hoseBun.status)} />
                            </TableCell>
                            <TableCell style={{color: textColor(hoseBun.expireDate)}}>
                              {hoseBun.expireDate ? moment(hoseBun.expireDate, 'YYYY-MM-DD').format('DD/MM/YYYY') : null}
                            </TableCell>
                            <TableCell>
                              {hoseBun.lastProducts}
                            </TableCell>
                          </TableRow>
                        ))
                      )
                    }
                  </TableBody>
                </Table>
              </TableContainer>
              <Pagination
                paginationVariables={paginationVariables}
                setPaginationVariables={setPaginationVariables}
                resultCount={resultCount}
                pageInfo={pageInfo}
              />
              <ButtonWrapper>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={addHoseBuns}
                  endIcon={<AddIcon />}
                  disabled={addButtonDisabled}
                >
                Add
                </Button>
              </ButtonWrapper>
            </>
          )
      }
    </Modal>
  );
}

const Link = styled.a`
  color: ${props => props.theme.colors.primaryColor};
  text-decoration: none;
`;

const Title = styled.div`
  color: ${props => props.theme.colors.primaryColor};
  font-weight: 700;
  margin-bottom: 36px;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;

  @media (${props => props.theme.breakpoints.tablet}) {
    justify-content: flex-start;
  }
`;

const StyledOrderIcon = styled(OrderDownIcon)`
  display: ${props => props.active ? 'inline-flex' : 'none'};
  transform: ${props => props.direction === 'desc' ? 'none' : 'rotate(180deg)'};
  margin-left: 4px;
  position: relative;
  top: 4px;
`;

export default HoseBunsModal;
